/* eslint-disable unicorn/no-for-loop */
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { FiArrowRight } from 'react-icons/fi';

import warningIcon from 'assets/warning_icon.png';
import apiService from 'services';
import { DATA_PERIOD, TIME_MAPPING_TYPE } from 'pages/ModelOverviewPage/data-revamp-new/Helper';
import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { useItemMapping } from 'hooks/ItemMapError';
import {
  fetchDimensionItems,
  getDistinctCSV,
  getEditActionForm,
  getNoheadersStatus,
  getSelectedAreaOfTable,
  getSyncOptions,
  handleActionStepNext,
  handleActionStepPrevious,
  setClearSelectedAreasTable,
  setPendingDataActionPayload,
  setSelectedAreasProperties,
} from 'redux/DataImportSlice';
import { getBlockDetails } from 'redux/PlanPageSlice';

import colors from '../../../dataTheme';
import { controlStyle } from '../../../detailUi';
import {
  FieldLabel,
  StepCheckbox,
  StepFooterRow,
  StepHeading,
  StepNotice,
  StepSelect,
  stepFooterButton,
} from '../../actionStepUi';

interface Properties {
  getFormValue: any;
}

const DimensionSelectionSteps = ({ getFormValue }: Properties) => {
  const dispatch: any = useDispatch();
  const distinctCSVValues = useSelector(getDistinctCSV);
  const blockDetails = useSelector(getBlockDetails);
  const dimensions = blockDetails?.dimensions;
  const [tabIndex, setTabIndex] = useState(-1);
  const [dimensionMap, setDimensionMap] = useState<Array<any>>([]);
  const [finalBlockDimMap, setFinalBlockDimMap] = useState<any>(null);
  const isHeader = useSelector(getNoheadersStatus);
  const dimensionMapLength = dimensionMap.length - 1;
  const [mappedColumnData, setMappedColumnData] = useState();
  const stepperFormEditable = useSelector(getEditActionForm);
  const area = useSelector(getSelectedAreaOfTable);
  const isDimension = dimensions && dimensions?.length === 1;

  useEffect(() => {
    if (dimensions && dimensions?.length) {
      const dimArray: any[] = dimensions
        .filter((dim: any) => dim.name !== 'Time')
        .map((dim: any) => {
          return {
            dim_id: dim.id,
            dim_name: dim.name,
            skip: true,
            column: null,
            item_map: [],
            dim_items: null,
            mapItemsManually: false,
          };
        });
      setDimensionMap(dimArray);
      setTabIndex(0);
    }
  }, [dimensions]);

  useEffect(() => {
    if (getFormValue?.columnIndicatorMappedData) {
      const mappedColumn: any = getFormValue?.columnIndicatorMappedData.reduce(
        (accumulator: any, item: any) => {
          accumulator[item.key] = item.value || null;
          return accumulator;
        },
        {},
      );
      setMappedColumnData(mappedColumn);
    }
  }, [getFormValue?.columnIndicatorMappedData]);

  useEffect(() => {
    if (dimensionMap) {
      const blockDimensionMap =
        dimensionMap &&
        dimensionMap?.map((items: any) => {
          // const newItemMap = items?.item_map
          //   ?.filter((item: any) => item[Object.keys(item)[0]] !== null)
          //   ?.map((itemId: any) => {
          //     const key = Object.keys(itemId)[0];
          //     const { id } = itemId[key];
          //     return { [key]: id };
          //   });
          let newItemMap = [];
          if (items?.item_map?.length > 0) {
            newItemMap = items.item_map
              .filter((item: any) => {
                const key = Object.keys(item)[0];
                return key && item[key] !== null && item[key] !== undefined;
              })
              .map((item: any) => {
                const key = Object.keys(item)[0];
                if (key && item[key] && item[key].id !== undefined) {
                  const { id } = item[key];
                  return { [key]: id };
                }
                return null;
              })
              .filter((mappedItem: any) => mappedItem !== null);
          }

          return {
            dim_id: items?.dim_id,
            skip: items?.skip,
            column: items?.column?.current || null,
            item_map: newItemMap,
            mapItemsManually: items?.mapItemsManually ?? false,
          };
        });
      setFinalBlockDimMap(blockDimensionMap);
    }
  }, [dimensionMap]);

  const syncOptions = useSelector(getSyncOptions);

  const onMapItemManually = (index: number, newValue: boolean) => {
    const dimArray = JSON.parse(JSON.stringify(dimensionMap));
    dimArray[index].mapItemsManually = newValue;

    if (!newValue) {
      // Switching back to auto-match: clear any stale explicit item mappings so
      // the backend receives an empty item_map and uses name-matching instead.
      const { column } = dimArray[index];
      const dimItems = dimArray[index].dim_items ?? [];
      if (column?.distinctValues) {
        dimArray[index].item_map = column.distinctValues.map((csvValue: string) => {
          const autoMatch = dimItems.find(
            (d: any) => d.name?.toLowerCase() === csvValue?.toLowerCase(),
          );
          return { [csvValue]: autoMatch ?? null };
        });
      } else {
        dimArray[index].item_map = [];
      }
    }

    setDimensionMap(dimArray);
  };

  useEffect(() => {
    if (getFormValue?.dimensionItemsData && distinctCSVValues && dimensions && dimensions?.length) {
      const dimArray: any[] = [];
      const promiseArray: any[] = [];
      const filteredDimensions: Array<any> = dimensions.filter((dim: any) => dim.name !== 'Time');
      filteredDimensions.map((dim: any) => {
        promiseArray.push(
          apiService.get_dimension_items(Number(dim.id), {
            params: { scenario_id: getFormValue?.selectedScenario },
          }),
        );
        return dim;
      });
      Promise.all(promiseArray).then((itemsResponse: any) => {
        for (let dimIndex = 0; dimIndex < filteredDimensions.length; dimIndex += 1) {
          const dim: any = filteredDimensions[dimIndex];
          const dimItem: any = getFormValue?.dimensionItemsData.find((object: any) => {
            return object.dim_id === dim.id;
          });
          const item: any = dimItem ? JSON.parse(JSON.stringify(dimItem)) : null;
          if (item && !item.skip) {
            item.dim_items = itemsResponse[dimIndex].data.items;
            const dimManual = item.mapItemsManually ?? getFormValue?.mapItemsManually ?? false;
            const column = distinctCSVValues.find((cool: any) => {
              return cool.current === item.column;
            });
            if (column) {
              item.column = column;

              const dimMapping: any = column.distinctValues.map((eachValue: string) => {
                const foundMappedObject = item.item_map.find((eachObject: any) => {
                  return eachObject[eachValue];
                });
                const itemId =
                  foundMappedObject && eachValue in foundMappedObject
                    ? foundMappedObject[eachValue]
                    : null;
                const foundItem: any = itemId
                  ? item.dim_items.find((eachObject: any) => {
                      return eachObject.id === itemId;
                    })
                  : !dimManual
                  ? item.dim_items.find((eachObject: any) => {
                      return eachObject.name === eachValue;
                    })
                  : null;

                // Edit Mapping with item Id and Map or Automatic Mapping in Edit mode
                return { [eachValue]: foundItem ?? null };
              });
              item.item_map = dimMapping;
            }

            if (item.column) item.skip = false;
            dimArray.push({
              dim_id: dim.id,
              dim_name: dim.name,
              skip: item.skip,
              column: item.column,
              item_map: item.item_map,
              dim_items: item.dim_items,
              mapItemsManually: dimManual,
            });
          } else {
            dimArray.push({
              dim_id: dim.id,
              dim_name: dim.name,
              skip: false,
              column: null,
              item_map: [],
              dim_items: null,
              mapItemsManually: false,
            });
          }
        }

        setDimensionMap(dimArray);
      });
    }
  }, [getFormValue?.dimensionItemsData, dimensions?.length, distinctCSVValues?.length]);

  const mappingSkipped = (index: number) => {
    const dimArray = JSON.parse(JSON.stringify(dimensionMap));
    dimArray[index].skip = true;
    dimArray[index].column = null;
    dimArray[index].item_map = [];
    setDimensionMap(dimArray);
  };

  const selectColumn = async (index: number, column: any) => {
    const dimArray = JSON.parse(JSON.stringify(dimensionMap));
    if (!dimArray[index]?.dim_items) {
      dispatch(
        fetchDimensionItems({
          dimensionId: Number(dimArray[index]?.dim_id),
          parameters: { scenario_id: getFormValue?.selectedScenario },
        }),
      ).then((response: any) => {
        const itemsData = response?.payload;
        dimArray[index].dim_items = itemsData;
        const dimMapping: any = column?.distinctValues?.map((eachValue: string) => {
          const findMatchData = itemsData?.find((item: any) => {
            return item?.name.toLowerCase() === eachValue.toLowerCase();
          });
          if (findMatchData) {
            return { [eachValue]: findMatchData };
          }
          return { [eachValue]: null };
        });
        dimArray[index].item_map = dimMapping;
        dimArray[index].column = column;
        if (dimArray[index]?.column) dimArray[index].skip = false;
        setDimensionMap(dimArray);
      });
    } else {
      dimArray[index].column = column;
      if (dimArray[index]?.column) dimArray[index].skip = false;
      setDimensionMap(dimArray);
    }
  };
  const mapItem = (index: number, item: string, columnIndex: number, dimItem: any) => {
    const dimArray = JSON.parse(JSON.stringify(dimensionMap));
    dimArray[index].item_map[columnIndex][item] = dimItem;
    setDimensionMap(dimArray);
  };

  const onSubmit = async () => {
    const usesTimeColumns =
      getFormValue?.dataPeriod === DATA_PERIOD.MULTIPLE &&
      getFormValue?.selectedTimePeriod === TIME_MAPPING_TYPE.COLUMN;
    const usesTimeRows =
      getFormValue?.dataPeriod === DATA_PERIOD.MULTIPLE &&
      getFormValue?.selectedTimePeriod === TIME_MAPPING_TYPE.ROW;
    const shouldUseDedicatedValueColumn = !usesTimeColumns;

    const object: any = {};
    if (
      getFormValue?.rowsIndicatorMappedData &&
      Object.keys(getFormValue?.rowsIndicatorMappedData)
    ) {
      Object.keys(getFormValue?.rowsIndicatorMappedData).map((key: string) => {
        if (getFormValue?.rowsIndicatorMappedData[key]) {
          object[getFormValue?.rowsIndicatorMappedData[key]] = key;
        }
        return key;
      });
    }
    if (mappedColumnData && Object.keys(mappedColumnData)) {
      Object.keys(mappedColumnData).map((key: string) => {
        if (mappedColumnData[key]) {
          object[mappedColumnData[key]] = key;
        }
        return key;
      });
    }
    const requestBody: any = {
      name: getFormValue?.name,
      description: getFormValue?.description,
      mappings: [
        {
          block_id: getFormValue?.blockId,
          time_column_map: usesTimeColumns ? getFormValue?.multiTimePeriodValue : null,
          time_value_map: usesTimeRows ? getFormValue?.timeValueMap : null,
          time_period_row_index: usesTimeColumns ? getFormValue?.timePeriodRowIndex : null,
          block_dimension_map: finalBlockDimMap ?? [],
          indicator_map: {
            ...object,
          },
          indicator: getFormValue?.singleIndicatorValue,
          indicator_column: getFormValue?.rowsIndicatorTableColumn ?? null,
          value_column: shouldUseDedicatedValueColumn
            ? getFormValue?.columnNumericValueoofRowsIndicatorType
            : null,
          shaddedArea: area,
        },
      ],
      scenario_id: getFormValue?.selectedScenario,
      mapping_type: getFormValue?.selectedTimePeriod,
      action_type: getFormValue?.dataType === '3' ? 'forecast' : 'data',

      time_column: usesTimeRows ? getFormValue?.singleTimePeriodValue : null,
      period_type: getFormValue?.dataPeriod === DATA_PERIOD.SINGLE ? 'single' : 'multiple',
      missing_header: isHeader,
      period_start_date: getFormValue?.startDate,
      is_multiple_indicator: getFormValue.importIndicatorType === '2',
      indicator_mapping_type: getFormValue?.multipleIndicatorsType,
      mapItemsManually: dimensionMap.some((dim) => !dim.skip && dim.mapItemsManually),
      // Pass through any sync_options already configured (e.g. clear_before_import)
      sync_options: syncOptions ?? null,
    };
    // Save payload to Redux for Sync Options step (last step) to create/update the action.
    dispatch(
      setPendingDataActionPayload({
        request_body: requestBody,
        isEditForm: !!stepperFormEditable?.isEditForm,
        actionId: stepperFormEditable?.actionId ?? undefined,
      }),
    );
    await dispatch(setClearSelectedAreasTable());
    dispatch(handleActionStepNext());
  };

  const handleMapItem = useCallback(
    (item: any, columnIndex: number, dimItem: any) => mapItem(tabIndex, item, columnIndex, dimItem),
    [tabIndex, mapItem],
  );

  const filteredItems = useMemo(() => {
    return dimensionMap[tabIndex]?.column?.distinctValues?.filter(
      (item: any) => item !== undefined && item !== '\n',
    );
  }, [dimensionMap, tabIndex]);

  const currentDimension = dimensionMap[tabIndex];

  const allItemsMapped = useItemMapping({ data: currentDimension?.item_map });

  return (
    <Box w='100%'>
      <StepHeading
        title='Select dimension columns'
        subtitle={
          isDimension
            ? undefined
            : `This block contains ${
                currentDimension?.dim_name ?? ''
              } information. Select the column which contains the list, or skip to import the totals.`
        }
      />
      {isDimension ? (
        <StepNotice>
          This block has no dimensions enabled besides Time. Dimension columns are not
          required—choose Continue to proceed with indicator and time mapping only.
        </StepNotice>
      ) : (
        <>
          <Flex gap={2} flexWrap='wrap' mb={4}>
            {dimensionMap.map((dim: any, index: number) => {
              const isActive = index === tabIndex;
              return (
                <Flex
                  key={dim.dim_id}
                  as='button'
                  type='button'
                  role='tab'
                  aria-selected={isActive}
                  h='28px'
                  px={3}
                  alignItems='center'
                  {...PLAN_TEXT.labelSmMedium}
                  color={isActive ? colors.primary : colors.textDark}
                  bg={isActive ? colors.activeBg : colors.surface}
                  border='1px solid'
                  borderColor={isActive ? colors.primary : colors.border}
                  borderRadius='full'
                  transition='background 0.12s ease, border-color 0.12s ease'
                  _hover={{ bg: isActive ? colors.activeBg : colors.hover }}
                  onClick={() => setTabIndex(index)}
                >
                  {dim.dim_name}
                </Flex>
              );
            })}
          </Flex>

          {currentDimension && (
            <>
              <FieldLabel>
                Select column containing {currentDimension.dim_name?.toLowerCase()}
              </FieldLabel>
              <Flex alignItems='center' gap={3}>
                <Box flex={1} minW={0}>
                  <StepSelect
                    value={currentDimension.column?.current ?? ''}
                    placeholder='Select column'
                    isClearable
                    options={(distinctCSVValues ?? [])
                      .filter((csv: any) => {
                        const foundIndex = dimensionMap.findIndex(
                          (dim_data: any) =>
                            dim_data?.column && dim_data?.column.current === csv?.current,
                        );
                        return foundIndex === -1 || foundIndex === tabIndex;
                      })
                      .map((item: any) => ({ value: item?.current, label: item?.modified }))}
                    onChange={(value) => {
                      const item = (distinctCSVValues ?? []).find(
                        (csv: any) => csv?.current === value,
                      );
                      selectColumn(tabIndex, item ?? null);
                      dispatch(
                        setSelectedAreasProperties({
                          [`dimSteps.${tabIndex}`]: item ? item.current : null,
                        }),
                      );
                    }}
                  />
                </Box>
                {dimensionMapLength > 0 && (
                  <Flex
                    as='button'
                    type='button'
                    {...controlStyle}
                    onClick={() => {
                      if (dimensionMapLength > tabIndex) {
                        setTabIndex(tabIndex + 1);
                      } else {
                        setTabIndex(0);
                      }
                      mappingSkipped(tabIndex);
                      dispatch(setSelectedAreasProperties({ [`dimSteps.${tabIndex}`]: null }));
                    }}
                  >
                    Skip
                  </Flex>
                )}
              </Flex>
            </>
          )}

          {currentDimension?.column && (
            <Flex direction='column' gap={3} mt={5}>
              <StepCheckbox
                isChecked={!!currentDimension.mapItemsManually}
                onChange={(next) => onMapItemManually(tabIndex, next)}
              >
                Map items manually
              </StepCheckbox>

              {!allItemsMapped && (
                <Flex
                  gap={2}
                  p={3}
                  alignItems='flex-start'
                  border='1px solid'
                  borderColor={colors.warning}
                  borderRadius={PLAN_REVAMP_RADIUS_SM}
                >
                  <Image src={warningIcon} height={4} width={4} mt='2px' />
                  <Box>
                    <Text {...PLAN_TEXT.bodyMedium} color={colors.textDark}>
                      Warnings
                    </Text>
                    <StepNotice>Oops! It seems a few items are unmapped.</StepNotice>
                  </Box>
                </Flex>
              )}

              <StepHeading title='Map the values into Blox' />

              <Flex alignItems='center' gap={3} mt='-8px'>
                <FieldLabel flex={1} mb={0}>
                  Name in file
                </FieldLabel>
                <Box w='16px' />
                <FieldLabel flex={1} mb={0}>
                  Item in Blox
                </FieldLabel>
              </Flex>
              <Flex direction='column' gap={3} maxH='25vh' overflowY='auto' className='scroll'>
                {filteredItems?.length > 0 ? (
                  filteredItems.map((item: any, columnIndex: number) => (
                    <Flex key={item} alignItems='center' gap={3}>
                      <Text
                        flex={1}
                        minW={0}
                        {...PLAN_TEXT.bodyMedium}
                        color={colors.textDark}
                        noOfLines={1}
                        title={item}
                      >
                        {item}
                      </Text>
                      <FiArrowRight size={16} color={colors.textSecondary} />
                      <Box flex={1} minW={0}>
                        <StepSelect
                          value={currentDimension?.item_map?.[columnIndex]?.[item]?.id ?? ''}
                          placeholder='Select item'
                          isClearable
                          isDisabled={!currentDimension?.mapItemsManually}
                          options={(currentDimension?.dim_items ?? [])
                            .filter((dimItem: any) => {
                              const foundIndex = currentDimension?.item_map?.findIndex(
                                (itemMap: any) =>
                                  itemMap[Object.keys(itemMap)[0]]?.id === dimItem?.id,
                              );
                              return (
                                foundIndex === -1 ||
                                currentDimension?.item_map?.[columnIndex]?.[item]?.id ===
                                  dimItem?.id
                              );
                            })
                            .map((dimItem: any) => ({ value: dimItem?.id, label: dimItem?.name }))}
                          onChange={(value) => {
                            const dimItem = (currentDimension?.dim_items ?? []).find(
                              (entry: any) => String(entry?.id) === String(value),
                            );
                            handleMapItem(item, columnIndex, dimItem ?? null);
                          }}
                        />
                      </Box>
                    </Flex>
                  ))
                ) : (
                  <StepNotice>No properties found</StepNotice>
                )}
              </Flex>
            </Flex>
          )}
        </>
      )}

      <StepFooterRow>
        <Flex gap={2}>
          <Button
            {...stepFooterButton}
            variant='outline'
            borderColor={colors.border}
            bg={colors.surface}
            color={colors.textDark}
            _hover={{ bg: colors.hover }}
            onClick={() => dispatch(handleActionStepPrevious())}
          >
            Previous
          </Button>
          <Button
            {...stepFooterButton}
            bg={colors.action}
            color={colors.white}
            _hover={{ bg: colors.actionHover }}
            onClick={onSubmit}
          >
            Continue
          </Button>
        </Flex>
      </StepFooterRow>
    </Box>
  );
};

export default DimensionSelectionSteps;
